import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart, Lock, Crown, Eye, EyeOff, Sparkles } from "lucide-react";
import { useWhoLikedMe } from "@/hooks/useWhoLikedMe";
import { formatDistanceToNow } from "date-fns";

interface WhoLikedMeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewProfile: (profileId: string) => void;
}

const WhoLikedMeModal = ({ isOpen, onClose, onViewProfile }: WhoLikedMeModalProps) => {
  const { likes, loading, isPremium, count } = useWhoLikedMe();
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());

  const handleReveal = (id: string) => {
    if (!isPremium) return;
    setRevealedIds((prev) => new Set(prev).add(id));
  };

  const handleViewProfile = (profileId: string) => {
    if (!isPremium) return;
    onViewProfile(profileId);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-like fill-like" />
            Who Liked You
            {count > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-like text-like-foreground">
                {count}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {/* Premium Upsell Banner */}
          {!isPremium && (
            <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">Upgrade to Premium</h3>
                  <p className="text-xs text-muted-foreground">
                    See who likes you before you match
                  </p>
                </div>
              </div>
              <Button className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                <Sparkles className="w-4 h-4 mr-2" />
                Get Premium
              </Button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 rounded-full border-2 border-like border-t-transparent animate-spin" />
            </div>
          ) : likes.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold mb-1">No likes yet</h3>
              <p className="text-sm text-muted-foreground">
                When someone likes you, they'll appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3 pb-4">
              {likes.map((like) => {
                const isRevealed = isPremium || revealedIds.has(like.id);
                const profile = like.profile;

                return (
                  <div
                    key={like.id}
                    className={`relative flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isRevealed
                        ? "bg-card border border-border hover:border-like/30 cursor-pointer"
                        : "bg-muted/50"
                    }`}
                    onClick={() => isRevealed && handleViewProfile(profile.id)}
                  >
                    {/* Profile Photo */}
                    <div className="relative w-14 h-14 rounded-full overflow-hidden bg-muted flex-shrink-0">
                      {profile.photo_url ? (
                        <img
                          src={profile.photo_url}
                          alt={isRevealed ? profile.name : "Hidden"}
                          className={`w-full h-full object-cover transition-all ${
                            isRevealed ? "" : "blur-lg scale-110"
                          }`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className={`text-xl font-bold ${isRevealed ? "" : "blur-md"}`}>
                            {profile.name?.[0] || "?"}
                          </span>
                        </div>
                      )}
                      {!isRevealed && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Lock className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Profile Info */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${!isRevealed && "blur-sm select-none"}`}>
                        {isRevealed ? profile.name : "Hidden User"}
                        {profile.age && isRevealed && (
                          <span className="text-muted-foreground font-normal">, {profile.age}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Liked you {formatDistanceToNow(new Date(like.created_at), { addSuffix: true })}
                      </p>
                    </div>

                    {/* Action */}
                    {!isRevealed && isPremium && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReveal(like.id);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    {!isPremium && (
                      <div className="p-2 rounded-full bg-amber-500/10">
                        <Crown className="w-4 h-4 text-amber-500" />
                      </div>
                    )}
                    {isRevealed && (
                      <Heart className="w-5 h-5 text-like fill-like" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhoLikedMeModal;
